/**
 * VraceniModal — "Přijmout vozidlo": the return of one reservation (P4-e). One surface for what the paper protocol
 * had: the km, the checklist read backwards, the deposit as a fact (returned or not), a note — and the car's damage
 * map next to it. The map is edited LOCALLY: the faults found (or repaired) are staged and only the difference travels
 * with the protocol, so the server applies it in the same transaction and a refused return leaves the real map
 * untouched. Same rules as the other rental modals: it renders only from reads that finished after it opened, freezes
 * while a request is in flight and cannot be dismissed then. A refusal (km below the handover's, …) is the server's
 * word, shown as it comes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useDilyQuery,
  usePredaniPripravaQuery,
  usePredaniQuery,
  useVratitMutation,
  useZavadyQuery,
  type Dil,
  type PredaniPriprava,
  type PujcovnaPredani,
  type Rezervace,
  type Zavada,
} from './api';
import { ZAVAZNOST_TEXT } from './ExteriorMap';
import {
  CISTOTA,
  KAUCE_ZPUSOB,
  PALIVO,
  PRAZDNY_FORMULAR,
  ZAPACH,
  digits,
  kauceKVraceni,
  type Cistota,
  type KauceZpusob,
  type Palivo,
  type PredaniForm,
  type Zapach,
} from './predani-helpers';
import { formatDateTime, formatTermin } from './rezervace-helpers';
import { PredaniFotky } from './PredaniFotky';
import {
  KONTROLNI_BODY_VRACENI,
  stageZavadu,
  unstageZavadu,
  vratZmenu,
  vraceniBody,
  zmenyZavad,
  zpozdeniText,
  type DruhZmeny,
} from './vraceni-helpers';
import { ZavadyEditor } from './ZavadyEditor';

const FAZE_TEXT: Record<Exclude<PredaniPriprava['faze'], 'vraceni'>, string> = {
  predani: 'Vozidlo ještě nebylo předáno — nejdřív ho předejte.',
  hotovo: 'Vozidlo je už vráceno — zápůjčka je ukončená.',
  nelze: 'Rezervace je zrušená — vozidlo nelze přijmout.',
};

const DRUH_ZMENY: Record<DruhZmeny, { label: string; tone: 'danger' | 'warn' }> = {
  nova: { label: 'Nová závada', tone: 'danger' },
  zmenena: { label: 'Změněná', tone: 'warn' },
};

const KAUCE_LABEL = Object.fromEntries(
  KAUCE_ZPUSOB.map((k) => [k.value, k.label.toLowerCase()]),
) as Record<KauceZpusob, string>;

/** One line about the handover the car left with, so the operator sees what to compare against. */
function souhrnPredani(p: PujcovnaPredani): string {
  const cast = [`Předáno ${formatDateTime(p.cas)}`, `${formatInt(p.km)} km`];
  if (p.kauce?.ano) {
    cast.push(
      `kauce${p.kauce.castka != null ? ` ${formatInt(p.kauce.castka)} Kč` : ''}${p.kauce.zpusob ? ` ${KAUCE_LABEL[p.kauce.zpusob]}` : ''}`,
    );
  }
  return cast.join(' · ');
}

interface VraceniModalProps {
  rezervace: Rezervace;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Info({
  testid,
  onClose,
  children,
  extra,
}: {
  testid: string;
  onClose: () => void;
  children: ReactNode;
  /** Read-only context under the message (the frozen photos of a protocol that is already written). */
  extra?: ReactNode;
}) {
  return (
    <>
      <Modal.Body className="px-8 py-5">
        <p data-testid={testid} className="text-[13px] text-ink-2">
          {children}
        </p>
        {extra && <div className="mt-5 flex flex-col gap-4">{extra}</div>}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={onClose}>
            Zavřít
          </Button>
        }
      />
    </>
  );
}

function VraceniFormular({
  rezervace,
  title,
  priprava,
  predani,
  stored: storedNow,
  parts,
  onOpenChange,
  onBusyChange,
}: {
  rezervace: Rezervace;
  title: string;
  priprava: PredaniPriprava;
  predani: PujcovnaPredani | undefined;
  stored: Zavada[];
  parts: Dil[];
  onOpenChange: (open: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const technicka = priprava.typ === 'technicka';
  const vratit = useVratitMutation(rezervace.id, rezervace.id_vozidla);
  // a photo in flight counts too: the dialog neither closes nor submits under it (the protocol freezes the photo set)
  const [fotkyBusy, setFotkyBusy] = useState(false);
  const busy = vratit.isPending || fotkyBusy;
  const [form, setForm] = useState<PredaniForm>(() => ({
    ...PRAZDNY_FORMULAR,
    km: priprava.km_posledni != null ? String(priprava.km_posledni) : '',
  }));
  // The map as it was when the dialog opened is the base the staged changes are diffed against: a background refetch
  // (window focus) must not turn somebody else's later edit into a "change" of ours.
  const [stored] = useState(storedNow);
  const [working, setWorking] = useState<Zavada[]>(stored);
  // keys each staged entry, so the part form re-mounts with the staged values
  const token = useRef(0);
  const set = <K extends keyof PredaniForm>(k: K, v: PredaniForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const zmeny = useMemo(() => zmenyZavad(stored, working), [stored, working]);

  // lift the busy state so the shell can refuse to dismiss while a request is in flight
  useBusy(busy, onBusyChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || form.km === '') return;
    vratit.mutate(vraceniBody(form, { technicka, zmeny }), {
      onSuccess: (res) => {
        const pozde = res.predani.priznaky?.po_terminu_min;
        const doplatek = res.predani.cena?.doplatek_km;
        const rozpis = res.predani.cena?.rozpis;
        toast.ok(
          [
            'Vozidlo přijato.',
            pozde != null ? `Vráceno ${zpozdeniText(pozde)} po termínu.` : '',
            rozpis ? `Pronájem za ${rozpis.dny} ${rozpis.dny === 1 ? 'den' : rozpis.dny < 5 ? 'dny' : 'dní'}: ${formatInt(rozpis.pronajem)} Kč.` : '',
            doplatek ? `Doplatek za nájezd navíc: ${formatInt(doplatek.castka)} Kč.` : '',
          ]
            .filter(Boolean)
            .join(' '),
        );
        onOpenChange(false);
      },
      onError: (err) =>
        toast.error(
          `Vozidlo nebylo přijato: ${(err as ApiError).message ?? 'Nepodařilo se zapsat vrácení.'}`,
        ),
    });
  };

  const nameOf = (id: number) => parts.find((p) => p.id_dilu === id)?.nazev ?? `Díl ${id}`;

  return (
    <>
      <Modal.Body className="px-8 py-5">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1 text-[13px] text-ink-2">
            <p>
              <span className="font-mono text-ink">{title}</span> ·{' '}
              {formatTermin(rezervace.od, rezervace.do)}
              {!technicka && rezervace.zakaznik_nazev && <> · {rezervace.zakaznik_nazev}</>}
            </p>
            {predani && (
              <p data-testid="vraceni-predano" className="font-mono text-[12px] text-ink-3">
                {souhrnPredani(predani)}
              </p>
            )}
          </div>

          <form
            id="vraceni-form"
            data-testid="vraceni-form"
            onSubmit={submit}
            className="flex flex-col gap-6"
          >
            <Fields cols={2}>
              <Field
                label="Nájezd při vrácení"
                required
                hint={
                  priprava.km_vydani != null
                    ? `Při předání: ${formatInt(priprava.km_vydani)} km.`
                    : undefined
                }
              >
                <TextInput
                  value={form.km}
                  inputMode="numeric"
                  suffix="km"
                  maxLength={7}
                  disabled={busy}
                  data-testid="vraceni-km"
                  onChange={(e) => set('km', digits(e.currentTarget.value))}
                />
              </Field>
              <Field label="Poznámka" optional="volitelné">
                <TextInput
                  value={form.poznamka}
                  maxLength={500}
                  disabled={busy}
                  data-testid="vraceni-poznamka"
                  onChange={(e) => set('poznamka', e.currentTarget.value)}
                />
              </Field>
            </Fields>

            {!technicka && (
              <>
                <section
                  data-testid="vraceni-seznam"
                  className="flex flex-col gap-3 border border-line p-4"
                >
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    Kontrolní seznam
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {KONTROLNI_BODY_VRACENI.map((b) => (
                      <Checkbox
                        key={b.key}
                        label={b.label}
                        checked={form.body[b.key]}
                        disabled={busy}
                        onChange={(e) =>
                          set('body', { ...form.body, [b.key]: e.currentTarget.checked })
                        }
                      />
                    ))}
                  </div>
                  <Fields cols={2}>
                    <Field label="Palivo" optional="volitelné">
                      <SelectInput
                        value={form.palivo}
                        disabled={busy}
                        data-testid="vraceni-palivo"
                        onChange={(e) => set('palivo', e.currentTarget.value as Palivo | '')}
                      >
                        <option value="">— nevyplněno —</option>
                        {PALIVO.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Čistota" optional="volitelné">
                      <SelectInput
                        value={form.cistota}
                        disabled={busy}
                        data-testid="vraceni-cistota"
                        onChange={(e) => set('cistota', e.currentTarget.value as Cistota | '')}
                      >
                        <option value="">— nevyplněno —</option>
                        {CISTOTA.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field label="Zápach" optional="volitelné">
                      <SelectInput
                        value={form.zapach}
                        disabled={busy}
                        data-testid="vraceni-zapach"
                        onChange={(e) => set('zapach', e.currentTarget.value as Zapach | '')}
                      >
                        <option value="">— nevyplněno —</option>
                        {ZAPACH.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                  </Fields>
                </section>

                <section
                  data-testid="vraceni-kauce"
                  className="flex flex-col gap-3 border border-line p-4"
                >
                  <Checkbox
                    label="Kauce vrácena zákazníkovi"
                    checked={form.kauce.ano}
                    disabled={busy}
                    onChange={(e) => {
                      const ano = e.currentTarget.checked;
                      // ticking offers what the handover recorded as taken (amount and way), unless something is typed already
                      const nabidka = kauceKVraceni(predani);
                      set('kauce', {
                        ano,
                        castka: ano && form.kauce.castka === '' ? nabidka.castka : form.kauce.castka,
                        zpusob: ano && form.kauce.zpusob === '' ? nabidka.zpusob : form.kauce.zpusob,
                      });
                    }}
                  />
                  <p data-testid="vraceni-kauce-slozena" className="text-[12px] text-ink-2">
                    {predani?.kauce?.ano
                      ? `Při předání složeno${predani.kauce.castka != null ? `: ${formatInt(predani.kauce.castka)} Kč` : ''}${predani.kauce.zpusob ? ` (${KAUCE_LABEL[predani.kauce.zpusob]})` : ''}.`
                      : 'Při předání kauce nebyla složena.'}
                  </p>
                  {form.kauce.ano && (
                    <Fields cols={2}>
                      <Field label="Vrácená částka" optional="volitelné">
                        <TextInput
                          value={form.kauce.castka}
                          inputMode="numeric"
                          suffix="Kč"
                          maxLength={6}
                          disabled={busy}
                          data-testid="vraceni-kauce-castka"
                          onChange={(e) =>
                            set('kauce', { ...form.kauce, castka: digits(e.currentTarget.value) })
                          }
                        />
                      </Field>
                      <Field label="Způsob" optional="volitelné">
                        <SelectInput
                          value={form.kauce.zpusob}
                          disabled={busy}
                          data-testid="vraceni-kauce-zpusob"
                          onChange={(e) =>
                            set('kauce', {
                              ...form.kauce,
                              zpusob: e.currentTarget.value as KauceZpusob | '',
                            })
                          }
                        >
                          <option value="">— nevyplněno —</option>
                          {KAUCE_ZPUSOB.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </SelectInput>
                      </Field>
                    </Fields>
                  )}
                  <p className="text-[12px] text-ink-3">
                    Zapisuje se jen fakt o kauci, žádná platba se tu neprovádí.
                  </p>
                </section>
              </>
            )}
          </form>

          <PredaniFotky rezervaceId={rezervace.id} typ="vydani" nadpis="Fotografie z předání" />
          <PredaniFotky
            rezervaceId={rezervace.id}
            typ="vraceni"
            disabled={vratit.isPending}
            onBusyChange={setFotkyBusy}
          />

          <section data-testid="vraceni-zavady" className="flex flex-col gap-3">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Závady vozu
            </div>
            <p className="text-[12px] text-ink-3">
              Zapište nové nebo změněné závady. Opravy se při zápůjčce nedělají — opravenou závadu označíte na stránce Závady vozu mezi zápůjčkami. Změny se uloží až s přijetím vozidla.
            </p>
            {/* outside the protocol form: the part form is a form of its own */}
            <ZavadyEditor
              faults={working}
              parts={parts}
              canWrite
              busy={busy}
              onSave={(dil, body) => {
                const t = ++token.current;
                setWorking((w) => stageZavadu(w, parts, dil, body, t));
              }}
              // no repair here (it is marked on the car's page); a fault staged in this dialog may be given up, a stored one stays
              onDelete={(f) => setWorking((w) => unstageZavadu(w, f.id_dilu))}
              canDelete={(f) => !stored.some((z) => z.id_dilu === f.id_dilu)}
              deleteLabel="Zrušit zapsanou závadu"
            />
            {zmeny.length > 0 && (
              <div
                data-testid="vraceni-zmeny"
                className="flex flex-col gap-2 border border-line p-3"
              >
                <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Změny závad při vrácení · {zmeny.length}
                </div>
                <ul className="flex flex-col gap-1.5">
                  {zmeny.map((z) => (
                    <li
                      key={z.id_dilu}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink"
                    >
                      <Badge tone={DRUH_ZMENY[z.druh].tone} size="sm">
                        {DRUH_ZMENY[z.druh].label}
                      </Badge>
                      <span>{nameOf(z.id_dilu)}</span>
                      <span className="text-[12px] text-ink-2 [overflow-wrap:anywhere]">
                        {`${ZAVAZNOST_TEXT[z.nove.zavaznost]}: ${z.nove.popis}`}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="ml-auto"
                        disabled={busy}
                        onClick={() => {
                          setWorking((w) => vratZmenu(stored, w, z.id_dilu));
                        }}
                      >
                        Vrátit zpět
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <p className="text-[12px] text-ink-3">
            Vrátí-li se vůz před domluveným koncem, rezervace se zkrátí na skutečné vrácení. Po
            termínu se nic nepřepisuje, zpoždění se jen zapíše do protokolu.
          </p>
        </div>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => !busy && onOpenChange(false)}
              disabled={busy}
            >
              Zrušit
            </Button>
            <Button
              variant="ink"
              type="submit"
              form="vraceni-form"
              disabled={busy || form.km === ''}
            >
              {busy ? 'Přijímám…' : 'Přijmout vozidlo'}
            </Button>
          </>
        }
      />
    </>
  );
}

function useBusy(busy: boolean, onBusyChange: (busy: boolean) => void) {
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);
}

export function VraceniModal({ rezervace, title, open, onOpenChange }: VraceniModalProps) {
  const priprava = usePredaniPripravaQuery(rezervace.id);
  const zavady = useZavadyQuery(rezervace.id_vozidla);
  const dily = useDilyQuery();
  const protokoly = usePredaniQuery(rezervace.id);
  const [busy, setBusy] = useState(false);
  // Built once from reads made AFTER the dialog opened (the map is the base the staged changes are diffed against).
  const fresh =
    priprava.isSuccess &&
    priprava.isFetchedAfterMount &&
    zavady.isSuccess &&
    zavady.isFetchedAfterMount &&
    protokoly.isSuccess &&
    protokoly.isFetchedAfterMount &&
    dily.isSuccess;
  const faze = priprava.data?.faze;
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={960}>
      <DialogCrown
        eyebrow="Půjčovna"
        title={
          <>
            Přijmout <em>vozidlo</em>
          </>
        }
        sub="Vrácení vozu: nájezd, stav vozu a závady. Po přijetí je zápůjčka ukončená a vůz je zase k dispozici."
      />
      {priprava.isError || zavady.isError || dily.isError || protokoly.isError ? (
        <Info testid="vraceni-error" onClose={() => guarded(false)}>
          Údaje k vrácení se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
        </Info>
      ) : !fresh || !priprava.data || !zavady.data || !dily.data ? (
        <>
          <Modal.Body className="px-8 py-5">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </Modal.Body>
          <Modal.Foot
            actions={
              <Button variant="secondary" onClick={() => guarded(false)}>
                Zrušit
              </Button>
            }
          />
        </>
      ) : faze !== 'vraceni' ? (
        <Info
          testid="vraceni-faze"
          onClose={() => guarded(false)}
          // both protocols are written once the car is back: their photos stay reachable (frozen, read-only)
          extra={
            faze === 'hotovo' ? (
              <>
                <PredaniFotky
                  rezervaceId={rezervace.id}
                  typ="vydani"
                  nadpis="Fotografie z předání"
                />
                <PredaniFotky
                  rezervaceId={rezervace.id}
                  typ="vraceni"
                  nadpis="Fotografie z vrácení"
                />
              </>
            ) : undefined
          }
        >
          {FAZE_TEXT[faze as Exclude<typeof faze, 'vraceni' | undefined>]}
        </Info>
      ) : (
        <VraceniFormular
          rezervace={rezervace}
          title={title}
          priprava={priprava.data}
          predani={protokoly.data?.predani.find((p) => p.typ === 'vydani')}
          stored={zavady.data}
          parts={dily.data}
          onOpenChange={onOpenChange}
          onBusyChange={setBusy}
        />
      )}
    </Modal>
  );
}
